// Data Models for ADDAPT Landing Page

export interface Profile {
  id: string;
  name: string;
  description: string;
  lastUpdated: string; // ISO date string
  createdBy: string;
  ldapGroup?: string;  // Optional - may not always be present
  status: 'active' | 'inactive' | 'processing';
}

export interface UserInfo {
  username: string;
  displayName: string;
  avatar?: string;
  preferences: {
    theme: 'light' | 'dark';
    locale: string;
  };
}

// Authentication Types

export interface CognitoConfig {
  region: string;
  userPoolId: string;
  userPoolWebClientId: string;
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
  responseType?: string;
}

// API Gateway configuration
export interface APIGatewayConfig {
  endpoints: Array<{
    name: string;
    endpoint: string;
  }>;
}

// WebSocket configuration  
export interface WebSocketConfig {
  default: string;
  docAgent: string;
}

export interface AuthUser {
  username: string;
  attributes?: {
    email?: string;
    name?: string;
    sub?: string;
    email_verified?: boolean;
    [key: string]: any;
  };
  signInUserSession?: {
    idToken: {
      jwtToken: string;
      payload: any;
    };
    accessToken: {
      jwtToken: string;
      payload: any;
    };
    refreshToken?: {
      token: string;
    };
  };
  // Formatted attributes (added after authentication)
  formattedAttributes?: {
    username: string;      // Formatted username without prefix
    givenName?: string;    // User's given name
    email?: string;        // User's email
    avatarUrl?: string;    // PhoneTool avatar URL
  };
}

export interface AppSettings {
  stage: 'local' | 'beta' | 'gamma' | 'production';
  cognitoConfig: CognitoConfig;
  apiConfig?: {
    endpoints: Array<{
      name: string;
      endpoint: string;
    }>;
  };
  features?: {
    enableAuthentication?: boolean;
    enableMockData?: boolean;
    enableDebugMode?: boolean;
    enableAnalytics?: boolean;
    enableWebSocket?: boolean;
  };
  mockUser?: {
    username: string;
    attributes: {
      email: string;
      name: string;
    };
    formattedAttributes: {
      username: string;
      givenName: string;
      email: string;
      avatarUrl: string;
    };
  };
}

export enum AUTH_STATES {
  SIGNING_IN = 'SIGNING_IN',
  SIGNED_IN = 'SIGNED_IN',
  SIGNED_OUT = 'SIGNED_OUT'
}

export enum USER_TYPE {
  INTERNAL = 'INTERNAL',
  EXTERNAL = 'EXTERNAL'
}

export interface TableSortingState {
  sortingColumn: {
    sortingField: string;
    sortingDescending: boolean;
  };
}

// Component Props Interfaces

export interface AppState {
  profiles: Profile[];
  loading: boolean;
  user: UserInfo;
}

export interface NavigationProps {
  user: UserInfo;
  onUserMenuClick: (event: any) => void;
}

export interface ProfilesSectionProps {
  profiles: Profile[];
  loading: boolean;
  onCreateProfile: () => void;
  onProfileSelect: (profileId: string) => void;
}

export interface ProfilesTableProps {
  profiles: Profile[];
  loading: boolean;
  onSelectionChange: (selectedItems: Profile[]) => void;
  onSortingChange: (sortingState: TableSortingState) => void;
  onProfileSelect?: (profileId: string) => void;
}

// ============================================
// Schedule Management Types
// ============================================

// Schedule Status Type - Aligned with AWS EventBridge Scheduler and internal Amazon standards
export type ScheduleStatus = 'ENABLED' | 'DISABLED' | 'AUTO_DISABLED';

// Schedule Frequency Type - Required by backend
export type ScheduleFrequencyType = 'CRON' | 'RATE';

// Schedule Execution Status Type
export type ScheduleExecutionStatus = 'SUCCEEDED' | 'FAILED' | 'RUNNING';

// Core Schedule Interface - Aligned with backend Smithy model
export interface Schedule {
  scheduleId: string;
  jobId: string;
  scheduleName: string;
  description?: string;
  scheduleExpression: string; // Full expression: cron(...) or rate(...)
  frequencyType: ScheduleFrequencyType;
  timezone?: string;
  status: ScheduleStatus;
  schedulerArn?: string;
  nextExecutionTime?: string; // ISO date string
  lastExecutionTime?: string; // ISO date string
  lastExecutionStatus?: string;
  executionCount?: number;
  consecutiveFailures?: number; // Number of consecutive failures
  lastFailureReason?: string; // Reason for last failure
  lastFailureAt?: string; // ISO date string of last failure
  autoDisabledAt?: string; // ISO date string when auto-disabled
  autoDisableReason?: string; // Reason for auto-disable
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
}

// Schedule Execution Interface
export interface ScheduleExecution {
  executionId: string;
  scheduleId: string;
  jobRunId: string;
  executionTime: string; // ISO date string
  status: ScheduleExecutionStatus;
  duration?: number; // milliseconds
  outputLocation?: string;
  errorMessage?: string;
}

// Schedule Statistics Interface
export interface ScheduleStats {
  totalExecutions: number;
  successRate: number; // 0-100
  averageDuration: number; // milliseconds
  consecutiveFailures: number;
}

// ============================================
// Schedule API Request/Response Types
// ============================================

// List Schedules
export interface ListSchedulesRequest {
  jobId: string;
  status?: ScheduleStatus;
  limit?: number;
  nextToken?: string;
  reconcile?: boolean;
}

export interface ListSchedulesResponse {
  schedules: Schedule[];
  nextToken?: string;
  count?: number;
}

// Create Schedule - Aligned with backend Smithy model
export interface CreateScheduleRequest {
  jobId: string;
  scheduleName: string;
  description?: string;
  scheduleExpression: string; // Full expression: cron(...) or rate(...)
  frequencyType: ScheduleFrequencyType;
  timezone?: string;
  enabled?: boolean;
}

// Update Schedule - Aligned with backend Smithy model
export interface UpdateScheduleRequest {
  jobId: string;
  scheduleId: string;
  scheduleName?: string;
  description?: string;
  scheduleExpression?: string;
  frequencyType?: ScheduleFrequencyType;
  timezone?: string;
}

// Update Schedule Status - Aligned with backend endpoint name
export interface UpdateScheduleStatusRequest {
  jobId: string;
  scheduleId: string;
  enabled: boolean;
}

// List Schedule Executions
export interface ListScheduleExecutionsRequest {
  scheduleId: string;
  limit?: number;
  nextToken?: string;
  statusFilter?: string[];
}

export interface ListScheduleExecutionsResponse {
  executions: ScheduleExecution[];
  stats: ScheduleStats;
  nextToken?: string;
}
